import { useState, useMemo, useRef, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown, X, Search } from "lucide-react";

/**
 * Searchable multi-select dropdown for sellers, grouped by seller_name so the same
 * underlying entity (multiple seller_ids → same name) is presented once. The component
 * returns the list of selected seller_ids (flattened), which is compatible with the
 * existing `quotation.seller_id IN (...)` filter logic.
 *
 * Props:
 *   sellers: Array<{ id, seller_name, seller_code?, city?, state?, shipping_address? }>
 *   selectedIds: string[]   — currently selected seller_ids.
 *   onChange: (ids: string[]) => void
 *   placeholder: string
 */
export default function SellerMultiSelect({ sellers = [], selectedIds = [], onChange, placeholder = "All Sellers" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const listRef = useRef(null);

  // Group sellers by normalized name so duplicates (same entity, diff id) collapse.
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of sellers) {
      const key = (s.seller_name || "").trim().toLowerCase();
      if (!key) continue;
      if (!map.has(key)) map.set(key, { key, display: s.seller_name, ids: [], meta: s });
      map.get(key).ids.push(s.id);
    }
    return Array.from(map.values()).sort((a, b) => a.display.localeCompare(b.display));
  }, [sellers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(g => {
      if (g.display.toLowerCase().includes(q)) return true;
      if (g.meta.seller_code && g.meta.seller_code.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [groups, search]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // A group is "selected" if ANY of its underlying ids is selected.
  const isGroupSelected = (g) => g.ids.some(id => selectedSet.has(id));

  const toggleGroup = (g) => {
    const next = new Set(selectedSet);
    if (isGroupSelected(g)) {
      g.ids.forEach(id => next.delete(id));
    } else {
      g.ids.forEach(id => next.add(id));
    }
    onChange(Array.from(next));
  };

  // Select All / Clear All operate on the *visible* filtered list for intuitive behavior.
  const handleSelectAll = () => {
    const ids = new Set(selectedSet);
    filtered.forEach(g => g.ids.forEach(id => ids.add(id)));
    onChange(Array.from(ids));
  };
  const handleClearAll = () => onChange([]);

  // "Selected" chips — group-level so each seller name shows once.
  const selectedGroups = useMemo(() => groups.filter(isGroupSelected), [groups, selectedIds]); // eslint-disable-line

  const allSelected = selectedIds.length === 0 || (filtered.length > 0 && filtered.every(isGroupSelected));

  // Focus the search input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => listRef.current?.querySelector("input[type=text]")?.focus(), 60);
    }
  }, [open]);

  // Trigger summary
  const summary = selectedGroups.length === 0
    ? placeholder
    : selectedGroups.length === 1
      ? selectedGroups[0].display
      : `${selectedGroups.length} sellers selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center justify-between gap-1.5 min-h-[36px] w-[240px] px-3 py-1.5 text-sm border border-input rounded-md bg-background hover:bg-accent/5 transition-colors"
          data-testid="seller-filter"
        >
          <span className={`truncate text-left ${selectedGroups.length === 0 ? "text-muted-foreground" : "text-foreground"}`}>
            {summary}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="p-0 w-[340px]" ref={listRef}>
        {/* Chips row */}
        {selectedGroups.length > 0 && (
          <div className="flex flex-wrap gap-1 p-2 border-b border-border max-h-[80px] overflow-y-auto">
            {selectedGroups.map(g => (
              <span
                key={g.key}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                style={{ background: "#E1F5EE", color: "#085041", border: "0.5px solid #32B4AC40" }}
                data-testid={`seller-chip-${g.meta.id}`}
              >
                {g.display}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleGroup(g); }}
                  className="ml-0.5 hover:bg-[#32B4AC]/20 rounded-full p-0.5"
                  aria-label={`Remove ${g.display}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative p-2 border-b border-border">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Type to search..."
            className="pl-7 h-8 text-sm"
            data-testid="seller-search-input"
          />
        </div>

        {/* Select All / Clear All */}
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-border">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleSelectAll}
            className="h-7 text-xs text-[#1D9E75] hover:text-[#1D9E75] hover:bg-[#E1F5EE]"
            data-testid="seller-select-all"
          >
            Select All
          </Button>
          {selectedGroups.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearAll}
              className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/5"
              data-testid="seller-clear-all"
            >
              Clear All
            </Button>
          )}
        </div>

        {/* List */}
        <div className="max-h-[280px] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-xs text-center text-muted-foreground">No sellers match your search.</p>
          ) : (
            filtered.map(g => {
              const selected = isGroupSelected(g);
              const m = g.meta || {};
              const addr = [m.city, m.state].filter(Boolean).join(", ");
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => toggleGroup(g)}
                  className={`w-full flex items-start gap-2 px-3 py-1.5 text-left hover:bg-accent/10 transition-colors ${selected ? "bg-[#E1F5EE]/40" : ""}`}
                  data-testid={`seller-option-${m.id}`}
                >
                  <Checkbox checked={selected} className="mt-1 pointer-events-none flex-shrink-0" />
                  <span className="flex flex-col min-w-0">
                    <span className="text-xs font-medium text-[#1A1A1A] truncate">
                      {g.display}
                      {m.seller_code && <span className="text-[#888780] font-mono ml-1">({m.seller_code})</span>}
                    </span>
                    {addr && <span className="text-[10px] text-[#888780] truncate">{addr}</span>}
                  </span>
                </button>
              );
            })
          )}
        </div>

        {!allSelected && (
          <p className="px-3 py-1.5 border-t border-border text-[10px] text-muted-foreground">
            {selectedIds.length === 0 ? "No seller selected → treated as All Sellers." : `${selectedGroups.length} of ${groups.length} sellers selected`}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
